/**
 * SHEEP MADE SIMPLE
 * Shared Seasonal Engine
 * Version 1.0.0
 */
window.SMSSeasonalEngine = (() => {
  "use strict";

  const ENGINE_VERSION = 1;
  const PLAN_SCHEMA_VERSION = 1;
  const definitions = new Map();
  const cache = new Map();
  const locks = new Set();

  function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function clone(value) {
    if (value === undefined) return undefined;
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  }

  function planKey(season, year) {
    return `${season}:${year}`;
  }

  function taskLockKey(season, year, taskId) {
    return `${season}:${year}:${taskId}`;
  }

  function registerSeason(definition) {
    if (!isPlainObject(definition)) throw new TypeError("A valid season definition is required.");
    const { season, taskOrder, validators = {}, normaliseTaskData = null } = definition;
    if (typeof season !== "string" || !season.trim()) throw new TypeError("Season is required.");
    if (!Array.isArray(taskOrder) || !taskOrder.length) throw new TypeError(`${season} requires taskOrder.`);
    taskOrder.forEach(taskId => {
      if (typeof taskId !== "string" || !taskId.trim()) throw new TypeError("Every task ID must be a string.");
      if (validators[taskId] && typeof validators[taskId] !== "function") {
        throw new TypeError(`Validator for ${season}.${taskId} must be a function.`);
      }
    });
    definitions.set(season, Object.freeze({
      season,
      taskOrder: Object.freeze([...taskOrder]),
      validators: Object.freeze({ ...validators }),
      normaliseTaskData: typeof normaliseTaskData === "function" ? normaliseTaskData : null
    }));
    return getDefinition(season);
  }

  function getDefinition(season) {
    return definitions.get(season) || null;
  }

  async function loadProfile() {
    if (window.SMSData && typeof window.SMSData.get === "function") {
      const profile = await window.SMSData.get();
      if (profile) return profile;
    }
    if (window.SMSFarmStorage && typeof window.SMSFarmStorage.loadProfile === "function") {
      return await window.SMSFarmStorage.loadProfile();
    }
    if (window.SMSFarmStorage && typeof window.SMSFarmStorage.get === "function") {
      return await window.SMSFarmStorage.get();
    }
    throw new Error("SMSFarmStorage is unavailable.");
  }

  async function persistSeasonalPlans(profile, context) {
    const partial = { seasonalPlans: profile.seasonalPlans };
    if (window.SMSData && typeof window.SMSData.update === "function") {
      return await window.SMSData.update(partial, context);
    }
    if (window.SMSFarmStorage && typeof window.SMSFarmStorage.update === "function") {
      return await window.SMSFarmStorage.update(partial, {
        sources: {
          [`seasonalPlans.${context.season}.${context.year}`]: {
            type: "seasonal_plan",
            key: context.sourceKey,
            savedAt: context.timestamp
          }
        },
        timestamp: context.timestamp
      });
    }
    if (window.SMSFarmStorage && typeof window.SMSFarmStorage.set === "function") {
      await window.SMSFarmStorage.set(profile);
      return profile;
    }
    throw new Error("No compatible SMSFarmStorage write method is available.");
  }

  function emptyPlan(season, year) {
    return {
      meta: {
        season,
        year,
        startedAt: null,
        updatedAt: null,
        version: PLAN_SCHEMA_VERSION
      },
      tasks: {}
    };
  }

  function getStoredPlan(profile, season, year) {
    const seasonStore = profile?.seasonalPlans?.[season];
    if (!seasonStore || !isPlainObject(seasonStore)) return null;
    const plan = seasonStore[String(year)] || seasonStore[year];
    return isPlainObject(plan) ? plan : null;
  }

  function ensurePlan(profile, season, year, timestamp) {
    profile.seasonalPlans ||= {};
    profile.seasonalPlans[season] ||= {};
    const seasonStore = profile.seasonalPlans[season];
    const yearKey = String(year);
    if (!isPlainObject(seasonStore[yearKey]) || !isPlainObject(seasonStore[yearKey].tasks)) {
      seasonStore[yearKey] = emptyPlan(season, year);
      seasonStore[yearKey].meta.startedAt = timestamp;
      seasonStore[yearKey].meta.updatedAt = timestamp;
    }
    seasonStore[yearKey].meta ||= {};
    seasonStore[yearKey].tasks ||= {};
    seasonStore[yearKey].meta.season = season;
    seasonStore[yearKey].meta.year = year;
    seasonStore[yearKey].meta.version = PLAN_SCHEMA_VERSION;
    seasonStore[yearKey].meta.startedAt ||= timestamp;
    return seasonStore[yearKey];
  }

  function validateRequest({ season, year, taskId, data }) {
    const errors = [];
    const definition = getDefinition(season);
    if (!definition) errors.push(`No Seasonal Engine definition is registered for ${season}.`);
    if (!Number.isInteger(year) || year < 2020 || year > 2100) errors.push("A valid plan year is required.");
    if (typeof taskId !== "string" || !taskId.trim()) errors.push("A valid taskId is required.");
    if (definition && !definition.taskOrder.includes(taskId)) {
      errors.push(`Task "${taskId}" is not registered for ${season}.`);
    }
    if (!isPlainObject(data)) errors.push("Task data must be an object.");
    return { valid: errors.length === 0, errors, definition };
  }

  function runTaskValidator(definition, taskId, data) {
    const validator = definition.validators[taskId];
    if (typeof validator !== "function") return { valid: true, errors: [] };
    try {
      const result = validator(clone(data));
      if (result === true || result === undefined) return { valid: true, errors: [] };
      if (result === false) return { valid: false, errors: ["Please complete all required fields."] };
      if (isPlainObject(result)) {
        const errors = Array.isArray(result.errors) ? result.errors.filter(Boolean) : [];
        return { valid: result.valid !== false && errors.length === 0, errors };
      }
      return { valid: true, errors: [] };
    } catch (error) {
      return { valid: false, errors: [error.message || "Task validation failed."] };
    }
  }

  async function saveTaskSnapshot({ season, year, taskId, data, completed = true, sourceKey = null }) {
    const request = validateRequest({ season, year, taskId, data });
    if (!request.valid) return { success: false, type: "validation", errors: request.errors };
    if (!window.SMSSeasonalTaskMetadata) {
      return { success: false, type: "dependency", errors: ["SMSSeasonalTaskMetadata is unavailable."] };
    }

    const key = taskLockKey(season, year, taskId);
    if (locks.has(key)) {
      return { success: false, type: "locked", errors: ["This decision is already being saved."] };
    }
    locks.add(key);

    try {
      const definition = request.definition;
      let taskData = clone(data);
      if (definition.normaliseTaskData) {
        taskData = definition.normaliseTaskData({ taskId, data: taskData });
      }
      const taskValidation = runTaskValidator(definition, taskId, taskData);
      if (!taskValidation.valid) {
        return { success: false, type: "validation", errors: taskValidation.errors };
      }

      const timestamp = new Date().toISOString();
      const profile = (await loadProfile()) || {};
      const plan = ensurePlan(profile, season, year, timestamp);
      const existingTask = plan.tasks[taskId] || null;

      const built = window.SMSSeasonalTaskMetadata.buildTaskRecord({
        season,
        year,
        taskId,
        data: taskData,
        completed,
        existingTask,
        allowedTaskIds: definition.taskOrder,
        timestamp
      });

      if (!built.success) {
        return { success: false, type: "validation", errors: built.errors };
      }

      plan.tasks[taskId] = built.task;
      plan.meta.updatedAt = timestamp;
      plan.meta.startedAt ||= timestamp;
      plan.meta.version = PLAN_SCHEMA_VERSION;

      const context = {
        source: "seasonal-plan",
        sourceKey: sourceKey || `${season}-plan-${year}`,
        page: `${season}-plan`,
        section: "seasonalPlans",
        season,
        year,
        taskId,
        action: "save-task-snapshot",
        timestamp
      };

      const savedProfile = await persistSeasonalPlans(profile, context);
      cache.set(planKey(season, year), clone(plan));

      const progress = calculateProgress(plan, definition.taskOrder);
      const summary = await getPlanSummary({ season, year, plan });

      const detail = { season, year, taskId, task: clone(built.task), plan: clone(plan), progress };
      document.dispatchEvent(new CustomEvent("sms:seasonal-task-saved", { detail }));
      document.dispatchEvent(new CustomEvent("sms:seasonal-summary-updated", { detail: { season, year, summary } }));

      return {
        success: true,
        type: "success",
        task: clone(built.task),
        plan: clone(plan),
        progress,
        summary,
        profile: savedProfile || profile
      };
    } catch (error) {
      console.error("[SMSSeasonalEngine] saveTaskSnapshot failed", error);
      return { success: false, type: "storage", errors: [error.message || "We couldn't save your changes."] };
    } finally {
      locks.delete(key);
    }
  }

  async function getPlan({ season, year, force = false }) {
    const definition = getDefinition(season);
    if (!definition) return { success: false, reason: "season_not_registered", data: null };
    const key = planKey(season, year);
    if (!force && cache.has(key)) return { success: true, data: clone(cache.get(key)), fromCache: true };
    try {
      const profile = await loadProfile();
      const stored = getStoredPlan(profile, season, year);
      const plan = stored ? clone(stored) : emptyPlan(season, year);
      cache.set(key, clone(plan));
      return { success: true, data: plan, fromCache: false, empty: !stored };
    } catch (error) {
      return { success: false, reason: "storage_error", errors: [error.message], data: null };
    }
  }

  async function getTask({ season, year, taskId, force = false }) {
    const planResult = await getPlan({ season, year, force });
    if (!planResult.success) return planResult;
    const task = planResult.data?.tasks?.[taskId] || null;
    return { success: true, data: task ? clone(task) : null, reason: task ? null : "task_not_found" };
  }

  function calculateProgress(plan, taskOrder) {
    const totalTasks = taskOrder.length;
    const completedTasks = taskOrder.filter(taskId => plan?.tasks?.[taskId]?.meta?.completed === true).length;
    return {
      totalTasks,
      completedTasks,
      remainingTasks: Math.max(0, totalTasks - completedTasks),
      percentage: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0
    };
  }

  async function getProgress({ season, year, plan = null }) {
    const definition = getDefinition(season);
    if (!definition) return { success: false, reason: "season_not_registered" };
    let resolvedPlan = plan;
    if (!resolvedPlan) {
      const result = await getPlan({ season, year });
      if (!result.success) return result;
      resolvedPlan = result.data;
    }
    return { success: true, ...calculateProgress(resolvedPlan, definition.taskOrder) };
  }

  function getTaskReviewStatus(task, { reviewDueDays = 30, staleDays = 60 } = {}) {
    const updatedAt = task?.meta?.updatedAt;
    if (!updatedAt || Number.isNaN(Date.parse(updatedAt))) {
      return { status: "unknown", daysSinceReview: null };
    }
    const daysSinceReview = Math.max(0, Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000));
    const status = daysSinceReview >= staleDays ? "stale" : daysSinceReview >= reviewDueDays ? "review_due" : "current";
    return { status, daysSinceReview };
  }

  async function getTaskSummary({ season, year, taskId, task = undefined }) {
    if (!window.SMSSeasonalSummary) return { success: false, reason: "summary_unavailable" };
    let resolvedTask = task;
    if (resolvedTask === undefined) {
      const result = await getTask({ season, year, taskId });
      if (!result.success) return result;
      resolvedTask = result.data;
    }
    const reviewStatus = resolvedTask ? getTaskReviewStatus(resolvedTask) : null;
    return {
      success: true,
      data: window.SMSSeasonalSummary.getTaskSummary({ season, year, taskId, task: resolvedTask, reviewStatus })
    };
  }

  async function getPlanSummary({ season, year, plan = null }) {
    if (!window.SMSSeasonalSummary) return { success: false, reason: "summary_unavailable" };
    let resolvedPlan = plan;
    if (!resolvedPlan) {
      const result = await getPlan({ season, year });
      if (!result.success) return result;
      resolvedPlan = result.data;
    }
    const reviewStatuses = {};
    Object.entries(resolvedPlan?.tasks || {}).forEach(([taskId, task]) => {
      reviewStatuses[taskId] = getTaskReviewStatus(task);
    });
    return {
      success: true,
      data: window.SMSSeasonalSummary.getPlanSummary({ season, year, plan: resolvedPlan, reviewStatuses })
    };
  }

  async function getCurrentFarmState({ season, year }) {
    const planResult = await getPlan({ season, year });
    if (!planResult.success) return planResult;
    const tasks = planResult.data.tasks || {};
    const state = { season, year };
    Object.values(tasks).forEach(task => {
      if (task?.data && isPlainObject(task.data)) Object.assign(state, clone(task.data));
    });
    return { success: true, data: state };
  }

  function clearCache({ season = null, year = null } = {}) {
    if (!season) {
      cache.clear();
      return;
    }
    if (year) cache.delete(planKey(season, year));
    else Array.from(cache.keys()).filter(key => key.startsWith(`${season}:`)).forEach(key => cache.delete(key));
  }

  const api = Object.freeze({
    ENGINE_VERSION,
    PLAN_SCHEMA_VERSION,
    registerSeason,
    getDefinition,
    saveTaskSnapshot,
    saveTask: saveTaskSnapshot,
    getTask,
    getPlan,
    getProgress,
    getTaskReviewStatus,
    getTaskSummary,
    getPlanSummary,
    getCurrentFarmState,
    clearCache
  });

  document.addEventListener("sms:farm-profile-changed", () => clearCache());
  document.addEventListener("sms:profile-updated", () => clearCache());
  document.addEventListener("sms:data-changed", () => clearCache());

  document.dispatchEvent(new CustomEvent("sms:seasonal-engine-ready", { detail: { version: ENGINE_VERSION } }));
  return api;
})();